import {memoize, pendingNetworkActionCountForHost} from '@servicenow/ui-internal';
import {createInteractionId, mark, types as metrics} from '@servicenow/ui-metrics';
import {getSelectorParts, getAffected} from '../property-selectors';
import {PROPERTY_SELECTOR_SIGIL, COMPONENT_INTERACTIVE} from '../constants';
import {
	CHILDREN_TO_BE_INTERACTIVE,
	ANCESTOR_ROOT_NODE,
	COMPONENT_ID,
	PROPS,
	INTERACTIVE_TIMESTAMP,
	SELECTABLE_PROPERTIES,
	CONDITIONAL_DISPATCH
} from '../symbols';
import getHostVariables from './getHostVariables';
import {countHighPriorityTasks, countMediumPriorityTasks} from '../scheduler';

const isProvider = memoize(
	el => {
		const variables = getHostVariables(el);
		return variables[SELECTABLE_PROPERTIES] && variables[SELECTABLE_PROPERTIES].length;
	},
	{createCacheKey: el => el.tagName, profileName: 'isProvider'}
);

const areProvidersInteractive = (el, componentRegistry) => {
	const variables = getHostVariables(el);
	const properties = variables[PROPS];

	for (const k in properties) {
		const value = properties[k];
		const [id] = getSelectorParts(value);
		if (id) {
			const providerEl = componentRegistry[id];
			if (providerEl) {
				const providerVariables = getHostVariables(providerEl);
				if (providerVariables && !providerVariables[INTERACTIVE_TIMESTAMP]) return false;
			}
		}
	}

	return true;
};

const notifySubscribers = (el, componentRegistry) => {
	const variables = getHostVariables(el);
	const selectabledProperties = variables[SELECTABLE_PROPERTIES];
	const selectabledPropertiesLength = (selectabledProperties && selectabledProperties.length) || 0;

	for (let i = 0; i < selectabledPropertiesLength; i++) {
		const propertyName = selectabledProperties[i];
		const affectedComponents = getAffected(
			propertyName,
			variables[COMPONENT_ID],
			componentRegistry,
			false
		);

		for (const id in affectedComponents) {
			const [componentId] = id.split(PROPERTY_SELECTOR_SIGIL);
			const subscriberEl = componentRegistry[componentId];
			if (subscriberEl) markComponentInteractive(subscriberEl, componentRegistry);
		}
	}
};

export default function markComponentInteractive(el, componentRegistry) {
	const variables = getHostVariables(el);
	if (!variables || variables[INTERACTIVE_TIMESTAMP]) return;

	if (countHighPriorityTasks(el)) return false;
	if (countMediumPriorityTasks(el)) return false;
	if (pendingNetworkActionCountForHost(el)) return false;

	const childrenToBeInteractive = variables[CHILDREN_TO_BE_INTERACTIVE];
	if (!childrenToBeInteractive) return false;

	for (const k in childrenToBeInteractive) return false;

	const providersInteractive = areProvidersInteractive(el, componentRegistry);
	if (!providersInteractive) return false;

	const interactiveTimestamp = performance.now();
	variables[INTERACTIVE_TIMESTAMP] = interactiveTimestamp;
	mark(el, createInteractionId(), metrics.INTERACTIVE, {});
	variables[CONDITIONAL_DISPATCH](
		COMPONENT_INTERACTIVE,
		{host: el, interactiveTimestamp},
		{internal: true}
	);

	const parentEl = variables[ANCESTOR_ROOT_NODE];
	if (parentEl) {
		const parentVariables = getHostVariables(parentEl);
		delete parentVariables[CHILDREN_TO_BE_INTERACTIVE][variables[COMPONENT_ID]];
		markComponentInteractive(parentEl, componentRegistry);
	}

	if (isProvider(el)) notifySubscribers(el, componentRegistry);
}
